import { MdOutlineClear } from 'react-icons/md';
import { Button, Tab, Tabs } from '@ds';
import { FilterDropdown } from '@ui';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useInfiniteOpoListQuery } from '@/queries/opo';
import {
  OpoContactListFilters,
  OpoContactTypeFilter,
  OpoOrganizationOrderByOption,
  OpoPrimaryContactFilter,
} from '@/api/apis/opo';

type ContactFiltersProps = {
  filters: OpoContactListFilters;
  hasFilters: boolean;
  updateFilters: (
    filter: OpoContactListFilters[keyof OpoContactListFilters],
    type: keyof OpoContactListFilters,
  ) => void;
  clearFilters: () => void;
};

export const OpoContactFilters = ({ filters, hasFilters, updateFilters, clearFilters }: ContactFiltersProps) => {
  const {
    list: locationList,
    isLoading,
    hasNextPage,
    loadNextPage,
    setSearchValue: onLocationSearch,
  } = useAsyncSearchableList({
    query: useInfiniteOpoListQuery,
    defaultSortOrder: { orderBy: OpoOrganizationOrderByOption.Name, asc: true },
  });

  return (
    <div className="flex gap-7 items-center">
      <FilterDropdown
        title="OPO name"
        items={locationList}
        selectedKeys={filters.organizationIds}
        mode="multiple"
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        loadNextPage={loadNextPage}
        onSearchValue={onLocationSearch}
        onSelectedKeysChange={(values) => updateFilters(values, 'organizationIds')}
      />

      <Tabs
        radius="full"
        aria-label="primary opo contact filter"
        selectedKey={filters.primaryFilter}
        onSelectionChange={(value) => updateFilters(value as OpoPrimaryContactFilter, 'primaryFilter')}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key={OpoPrimaryContactFilter.All} title="All" />
        <Tab key={OpoPrimaryContactFilter.Primary} title="Primary contact" />
        <Tab key={OpoPrimaryContactFilter.Secondary} title="Not a primary contact" />
      </Tabs>

      <Tabs
        radius="full"
        aria-label="opo contact type filter"
        selectedKey={filters.contactType}
        onSelectionChange={(value) => updateFilters(value as OpoContactTypeFilter, 'contactType')}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key={OpoContactTypeFilter.All} title="All" />
        <Tab key={OpoContactTypeFilter.Contact} title="Contact" />
        <Tab key={OpoContactTypeFilter.User} title="User" />
      </Tabs>

      {hasFilters && (
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onPress={clearFilters}>
          Clear all
        </Button>
      )}
    </div>
  );
};
